export const ERUDA_STORAGE_KEY = 'eruda';
export const ERUDA_DEFAULT_OPEN_STORAGE_KEY = 'eruda_default_open';

// Helper to safely execute window/localStorage operations without crashing
const safe = <T>(fn: () => T, fallback: T): T => {
  try {
    return typeof window !== 'undefined' ? fn() : fallback;
  } catch {
    return fallback;
  }
};

const readQueryParam = (key: string): string | null =>
  safe(() => {
    const val = new URLSearchParams(window.location.search)
      .get(key)
      ?.toLowerCase();
    if (['true', '1'].includes(val!)) return 'true';
    if (['false', '0'].includes(val!)) return 'false';
    return null;
  }, null);

export const isErudaEnabled = (): boolean => {
  const queryValue = readQueryParam(ERUDA_STORAGE_KEY);
  if (queryValue) {
    safe(
      () => window.localStorage.setItem(ERUDA_STORAGE_KEY, queryValue),
      null
    );
    return queryValue === 'true';
  }
  return safe(
    () => window.localStorage.getItem(ERUDA_STORAGE_KEY) === 'true',
    false
  );
};

export const isErudaDefaultOpen = (): boolean => {
  const queryValue = readQueryParam(ERUDA_DEFAULT_OPEN_STORAGE_KEY);
  if (queryValue) {
    safe(
      () =>
        window.localStorage.setItem(ERUDA_DEFAULT_OPEN_STORAGE_KEY, queryValue),
      null
    );
    return queryValue === 'true';
  }

  return safe(
    () =>
      window.localStorage.getItem(ERUDA_DEFAULT_OPEN_STORAGE_KEY) === 'true',
    false
  );
};

/** Dynamically load + start the eruda console. */
const startEruda = async (): Promise<void> => {
  const { default: eruda } = await import('eruda');
  // @ts-expect-error attribute exists, type incorrect
  if (eruda._isInit) return;
  eruda.init();

  safe(
    () =>
      eruda.position({
        x: window.innerWidth - 40,
        y: window.innerHeight - 40,
      }),
    null
  );

  if (isErudaDefaultOpen()) eruda.show();
};

/**
 * Update the persisted enabled flag and start/stop the eruda console
 * immediately.
 */
export const setErudaEnabled = async (enabled: boolean): Promise<void> => {
  safe(
    () => window.localStorage.setItem(ERUDA_STORAGE_KEY, String(enabled)),
    null
  );

  if (enabled) {
    await startEruda();
  } else {
    // Remove from UI
    (await import('eruda')).default.destroy();
  }
};

/** Update the persisted "default open" flag and apply it immediately. */
export const setErudaDefaultOpen = async (
  defaultOpen: boolean
): Promise<void> => {
  safe(
    () =>
      window.localStorage.setItem(
        ERUDA_DEFAULT_OPEN_STORAGE_KEY,
        String(defaultOpen)
      ),
    null
  );
  if (!isErudaEnabled()) return;

  const { default: eruda } = await import('eruda');
  // @ts-expect-error attribute exists, type incorrect
  if (!eruda._isInit) return;

  if (defaultOpen) {
    eruda.show();
  } else {
    eruda.hide();
  }
};

/** Bootstrap entry point — call once during app startup. */
export const initEruda = async (): Promise<void> => {
  if (isErudaEnabled()) await startEruda();
};
